import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF7ED] font-sans text-gray-800 py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium mb-8 transition-colors">
          <ChevronLeft size={20} className="mr-1" />
          Back to Home
        </Link>
        
        <div className="bg-white rounded-3xl shadow-xl border border-orange-100 p-8 md:p-12">
          <h1 className="text-4xl font-bold font-heading mb-8 text-gray-900 border-b border-orange-100 pb-4">
            Privacy Policy
          </h1>
          
          <div className="space-y-8 text-gray-600 leading-relaxed">
            <p>
              At DocSprintAI, accessible from docsprintai.com, one of our main priorities is the privacy of our visitors. This Privacy Policy document contains types of information that is collected and recorded by DocSprintAI and how we use it.
            </p>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              <p>
                The personal information that you are asked to provide, and the reasons why you are asked to provide it, will be made clear to you at the point we ask you to provide your personal information.
              </p>
              <ul className="list-disc ml-6 mt-4 space-y-2">
                <li>Account Information: Name, email address, and password.</li>
                <li>Project Data: Product descriptions, ideas, and configurations you input into the system.</li>
                <li>Usage Data: Information on how you interact with our services.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p>
                We use the information we collect in various ways, including to:
              </p>
              <ul className="list-disc ml-6 mt-4 space-y-2">
                <li>Provide, operate, and maintain our website and AI services.</li>
                <li>Improve, personalize, and expand our website.</li>
                <li>Understand and analyze how you use our website.</li>
                <li>Develop new products, services, features, and functionality.</li>
                <li>Communicate with you for customer service and updates.</li>
                <li>Process your transactions and manage your subscription.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Data Security</h2>
              <p>
                We use industry-standard security measures to protect your information. However, no method of transmission over the Internet or method of electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Sharing Your Information</h2>
              <p>
                We do not sell your personal data. We may share information with service providers (like AI model providers or payment processors) only to the extent necessary to provide the services. These providers are bound by strict confidentiality agreements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. AI Model Processing</h2>
              <p>
                To provide our core features, your input data is processed by Large Language Models (LLMs). We ensure that our integration with these providers prioritizes data privacy and prevents your data from being used to train public models without your consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookies and Web Beacons</h2>
              <p>
                Like any other website, DocSprintAI uses 'cookies'. These cookies are used to store information including visitors' preferences, and the pages on the website that the visitor accessed or visited.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Data Protection Rights</h2>
              <p>
                We would like to make sure you are fully aware of all of your data protection rights. Every user is entitled to the following: The right to access, the right to rectification, the right to erasure, the right to restrict processing, the right to object to processing, and the right to data portability.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at <a href="mailto:sales@baigenlabs.com" className="text-orange-600 hover:underline">sales@baigenlabs.com</a>.
              </p>
            </section>
          </div>
          
          <div className="mt-12 pt-8 border-t border-orange-100 text-sm text-gray-400">
            Last updated: March 11, 2026
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
